export type RecurrenceFrequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";

export interface RecurrenceFormState {
    enabled: boolean;
    frequency: RecurrenceFrequency;
    interval: number;
    byDays: string[];        // ["MO","TU"] for weekly
    byMonthDay?: number;     // 1..31 for monthly
    until?: string | null;   // ISO date string "YYYY-MM-DD" (date-only)
}

export function buildRecurrenceRule(s: RecurrenceFormState): string | null {
    if (!s.enabled) return null;

    const parts: string[] = [];
    parts.push(`FREQ=${s.frequency}`);
    parts.push(`INTERVAL=${Math.max(1, s.interval || 1)}`);

    if (s.frequency === "WEEKLY") {
        if (s.byDays?.length) parts.push(`BYDAY=${s.byDays.join(",")}`);
    }

    if (s.frequency === "MONTHLY") {
        if (s.byMonthDay && s.byMonthDay >= 1 && s.byMonthDay <= 31) {
            parts.push(`BYMONTHDAY=${s.byMonthDay}`);
        }
    }

    if (s.until) {
        // date-only UNTIL expected by your parser: "yyyy-MM-dd"
        parts.push(`UNTIL=${s.until}`);
    }

    return parts.join(";");
}

export const DOW: { label: string; token: string }[] = [
    { label: "Mon", token: "MO" },
    { label: "Tue", token: "TU" },
    { label: "Wed", token: "WE" },
    { label: "Thu", token: "TH" },
    { label: "Fri", token: "FR" },
    { label: "Sat", token: "SA" },
    { label: "Sun", token: "SU" },
];

export function getAnchorDate(parentForm: any): Date | null {
    const s = parentForm.type === 2 ? parentForm.dueUtc : parentForm.startUtc;
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
}

export function defaultRecurrenceFromParent(form: any): RecurrenceFormState {
    const anchor = getAnchorDate(form);
    const dowToken = anchor ? ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][anchor.getUTCDay()] : "MO";
    const dom = anchor ? anchor.getUTCDate() : 1;

    return {
        enabled: false,
        frequency: "MONTHLY",
        interval: 1,
        byDays: [dowToken],
        byMonthDay: dom,
        until: null,
    };
}

export function parseRecurrenceRule(rule: string | null | undefined): RecurrenceFormState | null {
    if (!rule || !rule.trim()) return null;

    const parts = rule.split(";").map(p => p.trim()).filter(Boolean);
    const map = new Map<string, string>();

    for (const p of parts) {
        const [k, v] = p.split("=", 2);
        if (k && v) map.set(k.toUpperCase(), v);
    }

    const freq = (map.get("FREQ")?.toUpperCase() ?? "MONTHLY") as RecurrenceFrequency;

    const interval = Math.max(1, Number(map.get("INTERVAL") ?? 1) || 1);

    const byDays = (map.get("BYDAY") ?? "")
        .split(",")
        .map(x => x.trim().toUpperCase())
        .filter(Boolean);

    const byMonthDayRaw = map.get("BYMONTHDAY");
    const byMonthDay = byMonthDayRaw ? Number(byMonthDayRaw) : undefined;

    const untilRaw = map.get("UNTIL");
    const until = untilRaw && untilRaw.length >= 10 ? untilRaw.substring(0, 10) : null;

    return {
        enabled: true,
        frequency: freq,
        interval,
        byDays,
        byMonthDay: Number.isFinite(byMonthDay) ? byMonthDay : undefined,
        until,
    };
}
